/**
 * EndActionsSlot
 *
 * Right-aligned slot inside a field's `labelRow` that hosts caller-provided
 * end actions (typically `<FieldActionChips />`). Lives as its own primitive
 * so `StringField`, `NumberField`, and `TextAreaField` share the exact same
 * top-right anchor — same display, alignment, gap, flex-shrink and
 * margin-left across all three. ArrayField and DictionaryField use a
 * different layout (chip lives next to a mode toggle), so they keep their
 * own local style.
 *
 * Pure presentational: no state, no callbacks, no theming hooks beyond
 * Fluent tokens. The wrapping `<span>` is only rendered when `children` is
 * truthy (or a Preview toggle slot is requested), mirroring the previous
 * `endActions && (...)` guard at every call site.
 */

import { makeStyles, mergeClasses, tokens } from '@coreai/fluentui-react';
import type { ReactNode } from 'react';

const useStyles = makeStyles({
  root: {
    display: 'inline-flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalXXS,
    flexShrink: 0,
    marginLeft: 'auto',
  },
  // `display: contents` makes the portaled Preview chip a direct flex item, so
  // it shares the chips' gap and an empty slot adds no gap at all.
  previewSlot: {
    display: 'contents',
  },
});

export interface EndActionsSlotProps {
  /** End actions (e.g. FieldActionChips). When falsy, nothing renders. */
  children: ReactNode;
  /** Optional className passthrough for layout overrides. */
  className?: string;
  /**
   * Callback ref from `useFieldPreviewFocus().previewToggleSlotRef`. Hosts the
   * Preview toggle ahead of the end actions.
   */
  previewToggleSlotRef?: ((element: HTMLElement | null) => void) | undefined;
}

export const EndActionsSlot = ({ children, className, previewToggleSlotRef }: EndActionsSlotProps) => {
  const styles = useStyles();
  if (!children && !previewToggleSlotRef) return null;
  return (
    <span className={mergeClasses(styles.root, className)}>
      {previewToggleSlotRef && <span ref={previewToggleSlotRef} className={styles.previewSlot} />}
      {children}
    </span>
  );
};

EndActionsSlot.displayName = 'EndActionsSlot';
